import React, { useState } from 'react';
import { Rate } from '../types/Rate';
import { MoreHorizontalIcon, PlusIcon, GripVerticalIcon } from 'lucide-react';
export function Rates() {
  // Mock data based on the screenshot
  const [rates, setRates] = useState<Rate[]>([{
    id: '1',
    name: 'Test With Scuba Spa',
    description: 'Scuba spa test',
    roomType: 'DELUXE',
    promoCode: 'EXPERIENCEMALDIVES',
    baseNightlyRate: 100.0,
    cancellationPlan: 'NON_REFUNDABLE',
    status: 'inactive',
    isPromo: true
  }, {
    id: '2',
    name: 'Advance Booking with Free Seaplan Transfer',
    description: 'Plan your Maldivan escape early and enjoy the added luxury of a complimentary round-trip seaplane transfer from Malé to the resort. This exclusive offer rewards advance planners with significant savings on transportation costs.',
    roomType: 'DELUXE',
    promoCode: 'EXPERIENCEMALDIVES2025',
    baseNightlyRate: 100.0,
    cancellationPlan: 'FLEXIBLE',
    status: 'active',
    isPromo: true
  }]);
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roomTypeFilter, setRoomTypeFilter] = useState<string>('all');
  // Filter rates based on search term and filters
  const filteredRates = rates.filter(rate => {
    const matchesSearch = rate.name.toLowerCase().includes(searchTerm.toLowerCase()) || rate.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || statusFilter === 'active' && rate.status === 'active' || statusFilter === 'inactive' && rate.status === 'inactive';
    const matchesRoomType = roomTypeFilter === 'all' || rate.roomType === roomTypeFilter;
    return matchesSearch && matchesStatus && matchesRoomType;
  });
  // Format currency for display
  function formatCurrency(amount: number): string {
    return `$${amount.toFixed(2)}`;
  }
  // Format cancellation plan for display
  function formatCancellationPlan(plan: string): string {
    return plan.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }
  return <div className="space-y-6" data-id="element-4805">
      <div className="flex justify-between items-center" data-id="element-4806">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-4807">Rates</h1>
        <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center" data-id="element-4808">
          <PlusIcon size={18} className="mr-1" data-id="element-4809" />
          Add Rates
        </button>
      </div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3" data-id="element-4810">
        <input type="text" placeholder="Search rates..." className="border border-gray-300 rounded-md px-3 py-2 w-48" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-4811" />
        <select className="border border-gray-300 rounded-md px-3 py-2" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-4812">
          <option value="all" data-id="element-4813">Status</option>
          <option value="active" data-id="element-4814">Active</option>
          <option value="inactive" data-id="element-4815">Inactive</option>
        </select>
        <select className="border border-gray-300 rounded-md px-3 py-2" value={roomTypeFilter} onChange={e => setRoomTypeFilter(e.target.value)} data-id="element-4816">
          <option value="all" data-id="element-4817">Room Type</option>
          <option value="DELUXE" data-id="element-4818">Deluxe</option>
          <option value="SUPERIOR" data-id="element-4819">Superior</option>
          <option value="OVERWATER_VILLA" data-id="element-4820">Overwater Villa</option>
        </select>
      </div>
      {/* Rates List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-4821">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-4822">
          <thead className="bg-gray-50" data-id="element-4823">
            <tr data-id="element-4824">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4825">
                Details
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4826">
                Promo Code
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4827">
                Base Nightly Rate
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4828">
                Cancellation Plan
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4829">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4830">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-4831">
            {filteredRates.map(rate => <tr key={rate.id} data-id="element-4832">
                <td className="px-6 py-4" data-id="element-4833">
                  <div className="flex items-start" data-id="element-4834">
                    <GripVerticalIcon size={16} className="text-gray-400 mt-1 mr-2" data-id="element-4835" />
                    <div data-id="element-4836">
                      <div className="text-sm font-medium text-gray-900" data-id="element-4837">
                        {rate.name}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 max-w-lg" data-id="element-4838">
                        {rate.description}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-4839">
                  <div className="text-sm text-gray-900" data-id="element-4840">{rate.promoCode}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-4841">
                  <div className="text-sm text-gray-900" data-id="element-4842">
                    {formatCurrency(rate.baseNightlyRate)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-4843">
                  <div className="text-sm text-gray-900" data-id="element-4844">
                    {formatCancellationPlan(rate.cancellationPlan)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-4845">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${rate.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-4846">
                    {rate.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                  {rate.isPromo && <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-4847">
                      PROMO
                    </span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-4848">
                  <button className="text-gray-400 hover:text-gray-500" data-id="element-4849">
                    <MoreHorizontalIcon size={18} data-id="element-4850" />
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}